import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Gift, Eye, EyeOff, MailCheck } from "lucide-react";
import { DevLink } from "@/pages/AuthActionPages";

const BG =
  "https://images.unsplash.com/photo-1695721780267-9ce4a448ef05?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjBsdXh1cnklMjB0ZXh0dXJlJTIwZ29sZCUyMHNsYXRlfGVufDB8fHx8MTc4MTYzMDAyOXww&ixlib=rb-4.1.0&q=85";

export default function RegisterPage() {
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [pending, setPending] = useState(null);

  useEffect(() => {
    if (user) navigate("/");
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const data = await register(email.trim(), password, name);
      setPending(data);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Não foi possível criar a conta");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#09090b]">
      <div className="relative hidden lg:block">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/55" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <img src="/radarx-logo.png" alt="RadarX" className="h-10 w-auto" />
          </div>
          <div>
            <h1 className="font-head text-4xl sm:text-5xl font-light text-[#fafafa] leading-tight">
              Inteligência de <span className="text-[#c8aa6e]">Anúncios</span>
            </h1>
            <div className="mt-6 inline-flex items-center gap-2 border border-[#c8aa6e]/40 bg-[#c8aa6e]/10 px-4 py-2">
              <Gift className="w-4 h-4 text-[#c8aa6e]" />
              <span className="text-sm text-[#e4e4e7]">7 dias grátis · sem cartão</span>
            </div>
          </div>
          <div className="label-tiny">Dados de múltiplos sites · num só painel</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        {pending ? (
          <div className="w-full max-w-sm fade-up" data-testid="register-pending">
            <div className="flex items-center gap-3 mb-2">
              <MailCheck className="w-5 h-5 text-[#c8aa6e]" strokeWidth={1.5} />
              <h2 className="font-head text-2xl font-normal text-[#fafafa]">Confirme o seu e-mail</h2>
            </div>
            <p className="text-sm text-[#a1a1aa] leading-relaxed mb-4">{pending.message}</p>
            <ol className="text-xs text-[#71717a] space-y-1.5 list-decimal pl-4 mb-4">
              <li>Abra o link de confirmação que enviámos para <span className="text-[#fafafa]">{email}</span>.</li>
              <li>O administrador aprova o seu acesso.</li>
              <li>Recebe acesso e pode iniciar sessão.</li>
            </ol>
            <DevLink link={pending.dev_link} />
            <a href="/login" data-testid="register-go-login" className="mt-6 w-full bg-[#c8aa6e] text-black hover:bg-[#d4b982] px-4 py-2.5 font-medium text-sm flex items-center justify-center">Ir para o início de sessão</a>
          </div>
        ) : (
        <form onSubmit={submit} className="w-full max-w-sm fade-up" data-testid="register-form">
          <h2 className="font-head text-2xl font-normal text-[#fafafa]">Criar Conta</h2>
          <p className="text-sm text-[#71717a] mt-1 mb-8">Após confirmar o e-mail, o acesso é aprovado pelo administrador</p>

          <label className="label-tiny block mb-2">Nome</label>
          <input data-testid="register-name" value={name} onChange={(e) => setName(e.target.value)}
            className="w-full bg-[#09090b] border border-[#3f3f46] text-[#fafafa] placeholder-[#71717a] focus:border-[#c8aa6e] outline-none px-3 py-2.5 text-sm mb-5" placeholder="O teu nome" required />

          <label className="label-tiny block mb-2">Email</label>
          <input data-testid="register-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-[#09090b] border border-[#3f3f46] text-[#fafafa] placeholder-[#71717a] focus:border-[#c8aa6e] outline-none px-3 py-2.5 text-sm mb-5" placeholder="o.teu@email.com" required />

          <label className="label-tiny block mb-2">Palavra-passe</label>
          <div className="relative mb-6">
            <input data-testid="register-password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-[#09090b] border border-[#3f3f46] text-[#fafafa] placeholder-[#71717a] focus:border-[#c8aa6e] outline-none px-3 py-2.5 pr-10 text-sm" placeholder="Mínimo 6 caracteres" required />
            <button type="button" data-testid="toggle-password" onClick={() => setShowPassword((s) => !s)}
              aria-label={showPassword ? "Ocultar palavra-passe" : "Mostrar palavra-passe"}
              className="absolute inset-y-0 right-0 flex items-center px-3 text-[#71717a] hover:text-[#c8aa6e] transition-colors">
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          {error && (
            <div data-testid="register-error" className="text-[#ef4444] text-sm mb-4 border border-[#ef4444]/30 bg-[#ef4444]/5 px-3 py-2">{error}</div>
          )}

          <button data-testid="register-submit" type="submit" disabled={busy}
            className="w-full bg-[#c8aa6e] text-black hover:bg-[#d4b982] active:scale-[0.98] px-4 py-2.5 font-medium text-sm transition-all flex items-center justify-center gap-2 disabled:opacity-60">
            {busy && <Loader2 className="w-4 h-4 spin" />}
            Criar conta grátis
          </button>

          <p className="text-sm text-[#71717a] mt-6 text-center">
            Já tens conta?{" "}
            <a href="/login" data-testid="go-login" className="text-[#c8aa6e] hover:text-[#d4b982]">Iniciar sessão</a>
          </p>
        </form>
        )}
      </div>
    </div>
  );
}
